import { useCallback, useEffect, useMemo } from "react";

import type { DBPost, PostModel } from "shared/types/PostModel";
import { postsDBConfig } from "shared/types/PostModel";

import { getDateTime } from "utils/getDateTime";

import { requestToPromise } from "utils/serviceUtils/dbUtils";

import usePostsDBEvents from "./usePostsDBEvents";

const convertPostToDBPost = (post: PostModel): DBPost => {
	let publishedAt = 0;
	if (post?.publishedAt) {
		publishedAt = getDateTime(post.publishedAt).toMillis();
	}

	let pinnedAt: null | number = null;
	if (post?.pinnedAt) {
		pinnedAt = getDateTime(post.pinnedAt).toMillis();
	}

	return {
		...post,
		publishedAt,
		pinnedAt
	};
};

const usePostsDB = () => {
	const { emit: emitDbEvent, destroy } = usePostsDBEvents();

	useEffect(() => {
		return () => {
			destroy();
		};
	}, [destroy]);

	const openDb = useCallback((): Promise<IDBDatabase> => {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(postsDBConfig.name, postsDBConfig.version);

			request.onupgradeneeded = () => {
				const db = request.result;

				if (!db.objectStoreNames.contains(postsDBConfig.stores.posts.name)) {
					const postsStore = db.createObjectStore(postsDBConfig.stores.posts.name, {
						keyPath: "id"
					});
					postsStore.createIndex("publishedAt", "publishedAt", { unique: false });
					postsStore.createIndex("pinnedAt", "pinnedAt", { unique: false });
					postsStore.createIndex("creatorId", "creator.id", { unique: false });
				}
			};

			request.onsuccess = () => resolve(request.result);
			request.onerror = () => reject(request.error);
		});
	}, []);

	const methods = useMemo(
		() => ({
			updatePosts: async (posts: PostModel[], removeExisted = false): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(postsDBConfig.stores.posts.name, "readwrite");

				const postStore = tx.objectStore(postsDBConfig.stores.posts.name);

				for (const post of posts) {
					const dbPost = convertPostToDBPost(post);
					const existingPost = (await requestToPromise(postStore.get(dbPost.id))) as DBPost | undefined;

					postStore.put({
						...existingPost,
						...dbPost
					});
				}

				if (removeExisted) {
					const postIds = new Set(posts.map(p => p.id));
					const allPosts = await requestToPromise(postStore.getAll());

					for (const existingPost of allPosts as DBPost[]) {
						if (!postIds.has(existingPost.id)) {
							postStore.delete(existingPost.id);
						}
					}
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "posts-changed" });
			},
			removePosts: async (postIds: string[]): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(postsDBConfig.stores.posts.name, "readwrite");

				const postStore = tx.objectStore(postsDBConfig.stores.posts.name);

				for (const postId of postIds) {
					postStore.delete(postId);
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "posts-changed" });
			},
			getPosts: async (params?: { creatorId?: string }) => {
				const db = await openDb();
				const tx = db.transaction(postsDBConfig.stores.posts.name, "readonly");
				const store = tx.objectStore(postsDBConfig.stores.posts.name);

				let posts: DBPost[];
				if (params?.creatorId) {
					const creatorIndex = store.index("creatorId");
					posts = (await requestToPromise(creatorIndex.getAll(params.creatorId))) as DBPost[];
				} else {
					posts = (await requestToPromise(store.getAll())) as DBPost[];
				}

				db.close();

				return posts.sort((a, b) => {
					// First, sort by pinned
					if (a.pinnedAt && !b.pinnedAt) return -1;
					if (!a.pinnedAt && b.pinnedAt) return 1;

					return b.publishedAt - a.publishedAt;
				});
			},
			cleanUpDB: async (): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction([postsDBConfig.stores.posts.name], "readwrite");

				const postsStore = tx.objectStore(postsDBConfig.stores.posts.name);
				postsStore.clear();

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();
			}
		}),
		[emitDbEvent, openDb]
	);

	return methods;
};

export default usePostsDB;
